import { ChevronDownIcon } from "@/components/icons/chevron-down-icon";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo, useCallback, useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { SelectOptionsSheet } from "./select-options-sheet";

export interface SelectOption {
  value: string;
  name: string;
}

interface SelectProp<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  placeholder?: string;
  disabled?: boolean;
  options: SelectOption[];
  multiple?: boolean;
  selectTitle: string;
}

/**
 * Returns the display label for the current value (selected option name, or empty).
 */
function getDisplayValue(value: unknown, options: SelectOption[]): string {
  if (value === undefined || value === null || value === "") return "";
  const found = options.find((opt) => opt.value === value);
  return found?.name ?? "";
}

function SelectInner<T extends FieldValues>({
  control,
  name,
  placeholder = "Select...",
  disabled = false,
  options,
  selectTitle,
}: SelectProp<T>) {
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const openSheet = useCallback(() => {
    if (!disabled) setIsSheetOpen(true);
  }, [disabled]);

  const closeSheet = useCallback(() => setIsSheetOpen(false), []);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const displayValue = getDisplayValue(field.value, options);

        const handleSelect = (value: string) => {
          field.onChange(value);
          closeSheet();
        };

        return (
          <>
            {/* Textfield-style trigger: same look as TextField, read-only, opens bottom sheet on click */}
            <div className="relative w-full">
              <button
                type="button"
                disabled={disabled}
                onClick={openSheet}
                className={cn(
                  textVariants.inputText,
                  "inline-flex h-11.5 w-full items-center rounded-xl border bg-white px-4 py-3 text-left text-ellipsis text-black",
                  "placeholder:text-text-placeholder-default focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400",
                  "focus:ring-primary border-gray-200",
                  "pr-12",
                )}
              >
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate",
                    !displayValue && "text-text-placeholder-default",
                  )}
                >
                  {displayValue || placeholder}
                </span>
              </button>
              <span
                className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400"
                aria-hidden
              >
                <ChevronDownIcon className="h-6 w-6" />
              </span>
            </div>

            <SelectOptionsSheet
              isOpen={isSheetOpen}
              onDismiss={closeSheet}
              options={options}
              selectTitle={selectTitle}
              disabled={disabled}
              onSelect={handleSelect}
            />
          </>
        );
      }}
    />
  );
}

const Select = memo(SelectInner) as typeof SelectInner;

export default Select;
